import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';

const milestones = [
  { year: '2018', title: '研究中心成立', desc: '白洁传研究中心正式成立，汇聚多位民间文学研究学者，开启系统化研究之路。' },
  { year: '2019', title: '首版整理本发布', desc: '历时两年整理完成白洁传首版权威整理本，获得学界广泛认可。' },
  { year: '2020', title: '学术研讨会', desc: '成功举办首届白洁传学术研讨会，来自全国各地的学者共同探讨版本考据与文学价值。' },
  { year: '2021', title: '线上平台上线', desc: '白洁传研究网站正式上线，为更广泛的读者群体提供便捷的阅读与学习渠道。' },
  { year: '2022', title: '研究专著出版', desc: '出版《白洁传研究论丛》系列专著，系统呈现多年研究成果。' },
  { year: '2023', title: '读者社区建立', desc: '建立白洁传承读者社区，凝聚文学爱好者力量，推动民间文学的当代传播。' },
  { year: '2024', title: '数字化工程', desc: '启动白洁传数字化保护工程，对珍贵版本进行数字化存档与开放获取。' },
  { year: '2025', title: '国际交流', desc: '白洁传研究成果走向国际，在多个国际民间文学会议上进行专题报告。' },
];

const academicAchievements = [
  { label: '研究论文', value: '120+', desc: '发表学术论文' },
  { label: '整理版本', value: '35+', desc: '版本整理成果' },
  { label: '学术活动', value: '50+', desc: '研讨会与讲座' },
  { label: '读者社区', value: '50K+', desc: '活跃读者群体' },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('company_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      setSubmitting(false);
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9q2rpme3oq4jqumtflg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '';

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
        setFormStatus('error');
        setFormError('提交失败，请稍后重试');
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '白洁传研究中心',
    url: 'https://baijiezhuan.com',
    description: '致力于白洁传的研究、整理与传播，为读者提供全面深入的文学解读与文化分析。',
    foundingDate: '2018',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@baijiezhuan.com',
      contactType: 'customer service',
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 白洁传研究中心"
        description="白洁传研究中心致力于白洁传的研究、整理与传播。了解我们的品牌故事、学术声誉及联系方式。"
        keywords="白洁传研究中心,关于我们,白洁传研究,学术研究,联系方式"
        canonicalPath="/about"
        schemaData={organizationSchema}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>
          </div>

          {/* Brand Introduction */}
          <section className="px-4 md:px-6 pb-14">
            <div className="max-w-4xl mx-auto">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 text-center">
                关于我们
              </h1>
              <p className="mt-4 text-foreground-600 text-center text-base max-w-2xl mx-auto">
                白洁传研究中心致力于民间文学经典的研究、整理与传播
              </p>

              <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                    品牌故事
                  </h2>
                  <p className="mt-4 text-foreground-700 leading-relaxed">
                    白洁传研究中心成立于2018年，由一群对中国民间文学怀有深厚热爱的学者共同发起。我们深知白洁传作为民间文学瑰宝的独特价值——它不仅是一部引人入胜的叙事作品，更是特定历史时期社会风貌、文化心理和民间智慧的集大成者。
                  </p>
                  <p className="mt-4 text-foreground-700 leading-relaxed">
                    研究中心以"传承经典、深耕学术、服务大众"为宗旨，致力于白洁传的多维度研究。我们的工作涵盖版本考据、文献整理、人物分析、文化解读和原著赏析等多个领域，力求为读者呈现一部全面、立体的白洁传。
                  </p>
                  <p className="mt-4 text-foreground-700 leading-relaxed">
                    经过多年的深耕与发展，研究中心已积累了丰富的研究成果，包括多部学术专著、数十篇研究论文以及完整的版本资料库。我们相信，民间文学经典的当代传播，既需要严谨的学术研究作为基础，也需要通俗易懂的表达方式触达更广泛的读者群体。
                  </p>
                </div>
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20literary%20research%20center%20interior%20with%20traditional%20Chinese%20wooden%20bookshelves%20filled%20with%20ancient%20books%2C%20scholars%20discussing%20around%20wooden%20table%2C%20soft%20golden%20sunlight%20through%20large%20windows%2C%20elegant%20academic%20atmosphere%2C%20minimal%20composition&width=700&height=500&seq=about-brand-story&orientation=landscape"
                    alt="白洁传研究中心品牌故事"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Academic Achievements */}
          <section id="reputation" className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  学术声誉
                </h2>
                <p className="mt-3 text-foreground-600 text-sm">
                  多年深耕，硕果累累
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
                {academicAchievements.map((item) => (
                  <div key={item.label} className="bg-background-50 rounded-lg p-6 text-center">
                    <div className="text-3xl font-bold text-primary-600 font-heading">
                      {item.value}
                    </div>
                    <div className="mt-2 text-sm font-medium text-foreground-800">
                      {item.label}
                    </div>
                    <div className="mt-1 text-xs text-foreground-500">
                      {item.desc}
                    </div>
                  </div>
                ))}
              </div>

              {/* Timeline */}
              <h3 className="font-heading text-xl font-bold text-foreground-950 text-center mb-8">
                发展历程
              </h3>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2 hidden md:block"></div>
                <div className="space-y-6">
                  {milestones.map((item, index) => (
                    <div
                      key={item.year}
                      className={`relative flex flex-col md:flex-row gap-4 ${
                        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className="md:w-1/2"></div>
                      <div className="absolute left-4 md:left-1/2 top-4 w-3 h-3 rounded-full bg-primary-500 -translate-x-1/2 hidden md:block z-10"></div>
                      <div className="md:w-1/2">
                        <div className="bg-background-50 rounded-lg p-5 border border-background-200/70 ml-8 md:ml-0">
                          <span className="text-xs font-bold text-primary-600">{item.year}</span>
                          <h4 className="mt-1 font-heading text-sm font-semibold text-foreground-950">
                            {item.title}
                          </h4>
                          <p className="mt-2 text-xs text-foreground-600 leading-relaxed">
                            {item.desc}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section id="contact" className="py-14 md:py-20 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  联系我们
                </h2>
                <p className="mt-3 text-foreground-600 text-sm">
                  有任何关于白洁传的问题或合作意向，欢迎与我们取得联系
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1 space-y-4">
                  <div className="bg-background-100 rounded-lg p-5">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <h4 className="mt-3 font-heading text-sm font-semibold text-foreground-950">电子邮箱</h4>
                    <p className="mt-1 text-xs text-foreground-600">contact@baijiezhuan.com</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-5">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <h4 className="mt-3 font-heading text-sm font-semibold text-foreground-950">微信公众号</h4>
                    <p className="mt-1 text-xs text-foreground-600">白洁传研究中心</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-5">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <h4 className="mt-3 font-heading text-sm font-semibold text-foreground-950">工作时间</h4>
                    <p className="mt-1 text-xs text-foreground-600">周一至周五 9:00-18:00</p>
                  </div>
                </div>

                <div className="lg:col-span-2">
                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="bg-background-100 rounded-lg p-6 md:p-8"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                          姓名 <span className="text-accent-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                          邮箱 <span className="text-accent-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        主题
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="请输入消息主题"
                      />
                    </div>

                    <div className="mb-4">
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        消息内容 <span className="text-accent-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                        placeholder="请输入您的消息内容（最多500字）"
                      ></textarea>
                    </div>

                    {/* Anti-spam honeypot */}
                    <div className="contact-form-anti-spam">
                      <label htmlFor="company_alt_field">Company</label>
                      <input
                        type="text"
                        id="company_alt_field"
                        name="company_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    {formStatus === 'success' && (
                      <div className="mb-4 p-3 rounded-md bg-green-50 border border-green-200 text-green-700 text-sm">
                        消息已成功发送！我们会尽快与您联系。
                      </div>
                    )}

                    {formStatus === 'error' && (
                      <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
                        {formError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full sm:w-auto px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md font-medium text-sm whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          发送中...
                        </span>
                      ) : (
                        '发送消息'
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}